import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faTiktok,
  faLinkedinIn,
  faGoogle,
  faShopify,
} from "@fortawesome/free-brands-svg-icons";
import {
  faSpinner,
  faLayerGroup,
  faSearch,
  faTimesCircle,
  faChevronRight,
  faChevronDown,
  faBullhorn,
  faPause,
  faPlay,
  faCalendarAlt,
  faSort,
  faSortUp,
  faSortDown,
  faImage,
  faRectangleAd,
  faFileArrowDown,
  faCircleCheck,
  faXmark,
  faPencil,
  faCheck,
  faInfoCircle,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { getRequest, postRequest, getCampaignInsightsChunked } from "services/api/apiService";
import { toast } from "react-toastify";

// ─── Constants ───────────────────────────────────────────────────

const PLATFORMS = [
  { key: "all",      label: "All Platforms", icon: faGlobe,  iconColor: "text-[#221C51]" },
  { key: "tiktok",   label: "TikTok",    icon: faTiktok,     iconColor: "text-black" },
  { key: "meta",     label: "Meta",      icon: faFacebookF,  iconColor: "text-blue-600" },
  { key: "linkedin", label: "LinkedIn",  icon: faLinkedinIn, iconColor: "text-blue-700" },
  { key: "snapchat", label: "Snapchat",  emoji: "👻" },
  { key: "google",   label: "Google",    icon: faGoogle,     iconColor: "text-red-500" },
  { key: "taboola",  label: "Taboola",   emoji: "📡" },
  { key: "shopify-oauth", label: "Shopify", icon: faShopify, iconColor: "text-green-600" },
];

const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7 days" },
  { key: "30d",   label: "30 days" },
  { key: "90d",   label: "90 days" },
  { key: "365d",  label: "1 year" },
];

const STATUS_FILTERS = ["ALL", "ACTIVE", "PAUSED"];

const STATUS_CLASSES = {
  ACTIVE:       "bg-green-100 text-green-700 border border-green-200",
  ENABLE:       "bg-green-100 text-green-700 border border-green-200",
  RUNNING:      "bg-green-100 text-green-700 border border-green-200",
  PAUSED:       "bg-amber-100 text-amber-700 border border-amber-200",
  DISABLE:      "bg-amber-100 text-amber-700 border border-amber-200",
  CRAWLING:     "bg-blue-100 text-blue-700 border border-blue-200",
  NEED_TO_EDIT: "bg-orange-100 text-orange-700 border border-orange-200",
  CANCELLED:    "bg-red-100 text-red-600 border border-red-200",
  TERMINATED:   "bg-red-100 text-red-600 border border-red-200",
  DELETED:      "bg-gray-100 text-gray-500 border border-gray-200",
  ARCHIVED:     "bg-gray-100 text-gray-500 border border-gray-200",
};

// ─── Small Components ─────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const cls = STATUS_CLASSES[status] || "bg-gray-100 text-gray-400 border border-gray-200";
  const label =
    status === "ENABLE" ? "ACTIVE" :
    status === "DISABLE" ? "PAUSED" :
    (status || "UNKNOWN");
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
};

const MetricCell = ({ value, loading }) =>
  loading
    ? <span className="inline-block w-10 h-3 rounded bg-gray-200 animate-pulse" />
    : <span className="text-xs text-gray-600">{value}</span>;

const SummaryStat = ({ label, value, color = "text-gray-800", loading = false }) => (
  <div className="flex flex-col min-w-[60px]">
    <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">{label}</span>
    {loading
      ? <span className="mt-0.5 inline-block w-12 h-4 rounded bg-gray-200 animate-pulse" />
      : <span className={`text-sm font-bold ${color}`}>{value}</span>
    }
  </div>
);

const SearchInput = ({ value, onChange, placeholder, light = false }) => (
  <div className="relative">
    <FontAwesomeIcon
      icon={faSearch}
      className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs pointer-events-none ${light ? "text-gray-400" : "text-white/60"}`}
    />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`pl-7 pr-7 py-1.5 text-xs rounded-lg focus:outline-none w-40 ${
        light
          ? "border border-gray-200 focus:ring-2 focus:ring-[#221C51]/20"
          : "bg-white/10 border border-white/20 text-white placeholder-white/40 focus:bg-white/20"
      }`}
    />
    {value && (
      <FontAwesomeIcon
        icon={faTimesCircle}
        className={`absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-xs ${light ? "text-gray-400" : "text-white/60"}`}
        onClick={() => onChange("")}
      />
    )}
  </div>
);

const ResetFiltersButton = ({ onClick, light = false }) => (
  <button
    onClick={onClick}
    title="Reset filters"
    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
      light
        ? "border border-gray-200 text-gray-500 hover:bg-gray-50"
        : "bg-white/10 text-white/80 hover:bg-white/20 border border-white/20"
    }`}
  >
    <FontAwesomeIcon icon={faXmark} className="text-xs" />
    Reset
  </button>
);

const SortableHeader = ({ label, sortKey, sortConfig, onSort, className = "" }) => {
  const isAct = sortConfig.key === sortKey;
  return (
    <th className={`px-4 py-3 font-medium cursor-pointer select-none group ${className}`} onClick={() => onSort(sortKey)}>
      <span className="flex items-center gap-1">
        {label}
        <FontAwesomeIcon
          icon={isAct ? (sortConfig.dir === "asc" ? faSortUp : faSortDown) : faSort}
          className={`text-[10px] transition-colors ${isAct ? "text-white" : "text-white/30 group-hover:text-white/60"}`}
        />
      </span>
    </th>
  );
};

const SortableHeaderLight = ({ label, sortKey, sortConfig, onSort, className = "" }) => {
  const isAct = sortConfig.key === sortKey;
  return (
    <th className={`px-4 py-3 font-medium cursor-pointer select-none group ${className}`} onClick={() => onSort(sortKey)}>
      <span className="flex items-center gap-1">
        {label}
        <FontAwesomeIcon
          icon={isAct ? (sortConfig.dir === "asc" ? faSortUp : faSortDown) : faSort}
          className={`text-[10px] transition-colors ${isAct ? "text-[#221C51]" : "text-gray-300 group-hover:text-gray-400"}`}
        />
      </span>
    </th>
  );
};

const AdThumbnail = ({ url }) =>
  url ? (
    <img src={url} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-100 flex-shrink-0" />
  ) : (
    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
      <FontAwesomeIcon icon={faImage} className="text-gray-300 text-sm" />
    </div>
  );

// ─── Helpers ─────────────────────────────────────────────────────

const matchesStatus = (itemStatus, filter) => {
  if (filter === "ALL") return true;
  if (filter === "ACTIVE") return itemStatus === "ACTIVE" || itemStatus === "ENABLE";
  // LinkedIn campaigns/creatives are created in DRAFT (non-delivering, same
  // as PAUSED) rather than PAUSED — LinkedIn rejects a direct null->PAUSED
  // transition at creation. Treat DRAFT as PAUSED for filtering purposes.
  if (filter === "PAUSED") return itemStatus === "PAUSED" || itemStatus === "DISABLE" || itemStatus === "DRAFT";
  return false;
};

const isActive    = (s) => ["ACTIVE", "ENABLE"].includes(s);
const isToggleable = (s) => ["ACTIVE", "ENABLE", "PAUSED", "DISABLE", "DRAFT"].includes(s);

const getDateRange = (preset) => {
  const end = new Date(), start = new Date();
  if (preset === "7d")   start.setDate(start.getDate() - 7);
  if (preset === "30d")  start.setDate(start.getDate() - 30);
  if (preset === "90d")  start.setDate(start.getDate() - 90);
  if (preset === "365d") start.setDate(start.getDate() - 365);
  const fmt = (d) => d.toISOString().split("T")[0];
  return { start: fmt(start), end: fmt(end) };
};

const fmtNum   = (n) => { if (n == null || n === "") return "—"; const x = Number(n); if (isNaN(x)) return "—"; if (x >= 1e6) return `${(x/1e6).toFixed(1)}M`; if (x >= 1e3) return `${(x/1e3).toFixed(1)}K`; return x.toLocaleString(); };
const fmtSpend = (n) => { if (n == null || n === "") return "—"; const x = parseFloat(n); return isNaN(x) ? "—" : `$${x.toFixed(2)}`; };
const fmtCTR   = (n) => { if (n == null || n === "") return "—"; const x = parseFloat(n); return isNaN(x) ? "—" : `${x.toFixed(2)}%`; };

const fmtBudget = (c, platform) => {
  if (platform === "tiktok")    return c.budget ? `$${Number(c.budget).toFixed(0)}${c.budgetMode === "BUDGET_MODE_DAY" ? "/day" : " life."}` : "—";
  if (platform === "meta")      return c.dailyBudget ? `$${(c.dailyBudget/100).toFixed(0)}/day` : (c.lifetimeBudget ? `$${(c.lifetimeBudget/100).toFixed(0)} life.` : "—");
  if (platform === "snapchat")  return c.dailyBudgetMicro ? `$${(c.dailyBudgetMicro/1e6).toFixed(0)}/day` : (c.lifetimeMicro ? `$${(c.lifetimeMicro/1e6).toFixed(0)} life.` : "—");
  return "—";
};

// ─── Main Component ───────────────────────────────────────────────

const CampaignManager = () => {
  const [platform, setPlatform] = useState(null);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [linkedInAdAccountId, setLinkedInAdAccountId] = useState(null);
  const [datePreset, setDatePreset] = useState("7d");
  const [connectedSet, setConnectedSet] = useState(new Set());
  const [syncErrors, setSyncErrors] = useState({});

  useEffect(() => {
    getRequest("/api/connected-platforms/all")
      .then(data => {
        const names = (Array.isArray(data) ? data : []).map(p => p.name.toLowerCase());
        setConnectedSet(new Set(names));
      })
      .catch(() => {});
  }, []);

  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignStatusFilter, setCampaignStatusFilter] = useState("ALL");
  const [campaignSort, setCampaignSort] = useState({ key: null, dir: "asc" });

  // Metrics
  const [metrics, setMetrics] = useState({});
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // Budget editing
  const [editingBudgetId, setEditingBudgetId] = useState(null);
  const [budgetInput, setBudgetInput] = useState("");
  const [savingBudgetId, setSavingBudgetId] = useState(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Ad Groups
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [adGroups, setAdGroups] = useState([]);
  const [adGroupsLoading, setAdGroupsLoading] = useState(false);
  const [adGroupSearch, setAdGroupSearch] = useState("");
  const [adGroupStatusFilter, setAdGroupStatusFilter] = useState("ALL");
  const [adGroupSort, setAdGroupSort] = useState({ key: null, dir: "asc" });

  // Ads (3rd level)
  const [selectedAdGroup, setSelectedAdGroup] = useState(null);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adSearch, setAdSearch] = useState("");
  const [adStatusFilter, setAdStatusFilter] = useState("ALL");

  // Taboola Create Item modal
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [createItemForm, setCreateItemForm] = useState({ title: '', url: '', thumbnail_url: '', description: '', cta: '' });
  const [createItemImageMode, setCreateItemImageMode] = useState('url'); // 'url' | 'file'
  const [createItemFile, setCreateItemFile] = useState(null);
  const [createItemPreview, setCreateItemPreview] = useState('');
  const [createItemLoading, setCreateItemLoading] = useState(false);

  // ── Unified (All Platforms) state ────────────────────────────────
  const [unifiedCampaigns, setUnifiedCampaigns] = useState([]);
  const [unifiedLoading, setUnifiedLoading] = useState(false);
  const [breakevenRoas, setBreakevenRoas] = useState(null);
  const [unifiedSearch, setUnifiedSearch] = useState("");
  const [unifiedStatusFilter, setUnifiedStatusFilter] = useState("ALL");
  const [togglingUnifiedId, setTogglingUnifiedId] = useState(null);

  const campaignLabel = platform === "linkedin" ? "Campaign Groups" : "Campaigns";
  const adGroupLabel  = platform === "linkedin" ? "Campaigns" : platform === "snapchat" ? "Ad Squads" : platform === "taboola" ? "Items" : "Ad Groups";
  const adLabel       = platform === "linkedin" ? "Creatives" : "Ads";
  const hasInsights   = platform === "tiktok" || platform === "meta";

  const campaignsRef = useRef([]);
  useEffect(() => { campaignsRef.current = campaigns; }, [campaigns]);

  // ── Derived / sorted lists ────────────────────────────────────────

  const handleCampaignSort = (key) =>
    setCampaignSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  const handleAdGroupSort = (key) =>
    setAdGroupSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  const filteredCampaigns = useMemo(() => {
    let list = campaigns.filter(c =>
      c.name.toLowerCase().includes(campaignSearch.toLowerCase()) &&
      matchesStatus(c.status, campaignStatusFilter)
    );
    if (!campaignSort.key) return list;
    return [...list].sort((a, b) => {
      let av, bv;
      if (["spend","impressions","clicks","ctr"].includes(campaignSort.key)) {
        av = parseFloat(metrics[a.id]?.[campaignSort.key] || 0);
        bv = parseFloat(metrics[b.id]?.[campaignSort.key] || 0);
      } else if (campaignSort.key === "budget") {
        av = a.budgetValue || 0; bv = b.budgetValue || 0;
      } else {
        av = (a[campaignSort.key] || "").toString().toLowerCase();
        bv = (b[campaignSort.key] || "").toString().toLowerCase();
      }
      return (av < bv ? -1 : av > bv ? 1 : 0) * (campaignSort.dir === "asc" ? 1 : -1);
    });
  }, [campaigns, campaignSearch, campaignStatusFilter, campaignSort, metrics]);

  const filteredAdGroups = useMemo(() => {
    let list = adGroups.filter(g =>
      g.name.toLowerCase().includes(adGroupSearch.toLowerCase()) &&
      matchesStatus(g.status, adGroupStatusFilter)
    );
    if (!adGroupSort.key) return list;
    return [...list].sort((a, b) => {
      const av = (a[adGroupSort.key] || "").toString().toLowerCase();
      const bv = (b[adGroupSort.key] || "").toString().toLowerCase();
      return (av < bv ? -1 : av > bv ? 1 : 0) * (adGroupSort.dir === "asc" ? 1 : -1);
    });
  }, [adGroups, adGroupSearch, adGroupStatusFilter, adGroupSort]);

  const filteredAds = useMemo(() =>
    ads.filter(a => a.name.toLowerCase().includes(adSearch.toLowerCase()) && matchesStatus(a.status, adStatusFilter)),
    [ads, adSearch, adStatusFilter]
  );

  // ── Summary stats (computed from loaded data, no extra API calls) ─

  const summaryStats = useMemo(() => {
    if (!campaigns.length) return null;
    const active  = campaigns.filter(c => isActive(c.status)).length;
    const paused  = campaigns.filter(c => !isActive(c.status) && isToggleable(c.status)).length;
    const totalBudget = campaigns.reduce((s, c) => s + (c.budgetValue || 0), 0);
    const vals = Object.values(metrics);
    if (!hasInsights || !vals.length) return { active, paused, totalBudget, hasMetrics: false };
    const totalSpend  = vals.reduce((s, m) => s + parseFloat(m.spend || 0), 0);
    const totalImpr   = vals.reduce((s, m) => s + parseInt(m.impressions || 0), 0);
    const totalClicks = vals.reduce((s, m) => s + parseInt(m.clicks || 0), 0);
    const avgCTR      = totalImpr > 0 ? (totalClicks / totalImpr * 100) : 0;
    return { active, paused, totalBudget, totalSpend, totalImpr, totalClicks, avgCTR, hasMetrics: true };
  }, [campaigns, metrics, hasInsights]);

  // ── Bulk selection helpers ────────────────────────────────────────

  const allFilteredSelected =
    filteredCampaigns.length > 0 &&
    filteredCampaigns.every(c => selectedIds.includes(c.id));

  const handleSelectAll = (e) => {
    e.stopPropagation();
    if (allFilteredSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredCampaigns.find(c => c.id === id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...filteredCampaigns.map(c => c.id)])]);
    }
  };

  const handleSelectOne = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ── Core toggle (used by single + bulk) ──────────────────────────

  const toggleCampaignStatus = useCallback(async (campaign) => {
    const curr = isActive(campaign.status);
    let newStatus;
    if (platform === "tiktok") {
      newStatus = curr ? "DISABLE" : "ENABLE";
      await postRequest(`/api/ads/tiktok/campaign/${campaign.id}/status`, { status: newStatus });
    } else if (platform === "meta") {
      newStatus = curr ? "PAUSED" : "ACTIVE";
      await postRequest(`/api/ads/meta/campaign/${campaign.id}/status`, { status: newStatus });
    } else if (platform === "snapchat") {
      newStatus = curr ? "PAUSED" : "ACTIVE";
      await postRequest(`/api/ads/snapchat/campaign/${campaign.id}/status`, { status: newStatus });
    } else if (platform === "linkedin") {
      newStatus = curr ? "PAUSED" : "ACTIVE";
      await postRequest(`/api/ads/linkedin/ad-accounts/${linkedInAdAccountId}/campaign-groups/${campaign.id}/status`, { status: newStatus });
    } else if (platform === "google") {
      newStatus = curr ? "PAUSED" : "ACTIVE";
      const googleStatus = curr ? "PAUSED" : "ENABLED";
      await postRequest(`/api/ads/google/campaign/${campaign.id}/status`, { status: googleStatus });
    } else if (platform === "taboola") {
      newStatus = curr ? "PAUSED" : "ACTIVE";
      await postRequest(`/api/ads/taboola/campaign/${campaign.id}/status`, { is_active: !curr });
    } else {
      throw new Error("Toggle not supported for this platform.");
    }
    return newStatus;
  }, [platform, linkedInAdAccountId]);

  const handleToggleCampaign = async (campaign, e) => {
    e.stopPropagation();
    if (togglingId || !isToggleable(campaign.status)) return;
    setTogglingId(campaign.id);
    try {
      const newStatus = await toggleCampaignStatus(campaign);
      setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, status: newStatus } : c));
      toast.success(`Campaign ${isActive(campaign.status) ? "paused" : "resumed"}.`);
    } catch (err) {
      toast.error(err.message || "Failed to update campaign status.");
    } finally {
      setTogglingId(null);
    }
  };

  // ── Bulk pause / resume ───────────────────────────────────────────

  const handleBulkAction = async (action) => {
    const toProcess = selectedIds
      .map(id => campaigns.find(c => c.id === id))
      .filter(c => c && isToggleable(c.status) && (action === "pause" ? isActive(c.status) : !isActive(c.status)));

    if (!toProcess.length) {
      toast.info(`All selected campaigns are already ${action === "pause" ? "paused" : "active"}.`);
      return;
    }

    setBulkLoading(true);
    const updates = {};
    let count = 0;
    for (const campaign of toProcess) {
      try {
        updates[campaign.id] = await toggleCampaignStatus(campaign);
        count++;
      } catch (err) {
        console.warn(`Bulk ${action} failed for ${campaign.name}:`, err.message);
      }
    }
    setCampaigns(prev => prev.map(c => updates[c.id] ? { ...c, status: updates[c.id] } : c));
    setSelectedIds([]);
    setBulkLoading(false);
    toast.success(`${count} campaign(s) ${action === "pause" ? "paused" : "resumed"}.`);
  };

  // ── Inline budget edit ────────────────────────────────────────────

  const startEditBudget = (campaign, e) => {
    e.stopPropagation();
    const raw = campaign.budgetValue || 0;
    setEditingBudgetId(campaign.id);
    setBudgetInput(String(raw));
  };

  const cancelEditBudget = (e) => {
    e?.stopPropagation();
    setEditingBudgetId(null);
    setBudgetInput("");
  };

  const saveBudget = async (campaign, e) => {
    e?.stopPropagation();
    const val = parseFloat(budgetInput);
    if (isNaN(val) || val <= 0) { toast.error("Enter a valid budget amount."); return; }
    setSavingBudgetId(campaign.id);
    try {
      if (platform === "tiktok") {
        await postRequest(`/api/ads/tiktok/campaign/${campaign.id}/budget`, { budget: val });
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, budget: val, budgetValue: val } : c));
        toast.success("TikTok budget updated.");
      } else if (platform === "meta") {
        const budgetType = campaign.lifetimeBudget > 0 ? "lifetime" : "daily";
        await postRequest(`/api/ads/meta/campaign/${campaign.id}/budget`, { amount: val, budgetType });
        const newBudget = budgetType === "daily" ? { dailyBudget: val * 100, budgetValue: val } : { lifetimeBudget: val * 100, budgetValue: val };
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, ...newBudget } : c));
        toast.success("Meta budget updated.");
      } else if (platform === "snapchat") {
        await postRequest(`/api/ads/snapchat/campaign/${campaign.id}/budget`, { amount: val });
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, dailyBudgetMicro: val * 1e6, budgetValue: val } : c));
        toast.success("Snapchat budget updated.");
      } else {
        toast.info("Budget editing is not supported for this platform.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update budget.");
    } finally {
      setSavingBudgetId(null);
      setEditingBudgetId(null);
      setBudgetInput("");
    }
  };

  // ── Export CSV ────────────────────────────────────────────────────

  const handleExportCSV = () => {
    const hasObj = platform === "tiktok" || platform === "meta";
    const headers = ["ID", "Name", "Status", "Budget", "Spend", "Impressions", "Clicks", "CTR"];
    if (hasObj) headers.push("Objective");

    const rows = filteredCampaigns.map(c => {
      const m = metrics[c.id] || {};
      const statusLabel = c.status === "ENABLE" ? "ACTIVE" : c.status === "DISABLE" ? "PAUSED" : c.status;
      const row = [
        c.id,
        `"${(c.name || "").replace(/"/g, '""')}"`,
        statusLabel,
        `"${fmtBudget(c, platform)}"`,
        m.spend || "",
        m.impressions || "",
        m.clicks || "",
        m.ctr || "",
      ];
      if (hasObj) row.push(`"${c.objective || ""}"`);
      return row.join(",");
    });

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }); // BOM for Excel
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaigns_${platform}_${datePreset}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  // ── Insights ─────────────────────────────────────────────────────

  const loadInsights = useCallback(async (p, campaignList, preset) => {
    if (!["tiktok", "meta"].includes(p) || !campaignList.length) { setMetrics({}); return; }
    setMetricsLoading(true);
    try {
      const { start, end } = getDateRange(preset);
      const metricsMap = await getCampaignInsightsChunked(p, campaignList.map(c => c.id), start, end);
      setMetrics(metricsMap);
    } catch (err) {
      console.warn("Metrics load failed:", err.message);
      setMetrics({});
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  const loadUnifiedCampaigns = useCallback(async (preset) => {
    setUnifiedLoading(true);
    setUnifiedCampaigns([]);
    try {
      const { start, end } = getDateRange(preset);
      const data = await getRequest(`/api/unified/campaigns?start_date=${start}&end_date=${end}`);
      setUnifiedCampaigns(data.campaigns || []);
      setBreakevenRoas(data.breakeven_roas ?? null);
    } catch (err) {
      toast.error(err.message || "Failed to load unified campaigns.");
    } finally {
      setUnifiedLoading(false);
    }
  }, []);

  const filteredUnified = useMemo(() => {
    return unifiedCampaigns.filter(c =>
      (c.name || "").toLowerCase().includes(unifiedSearch.toLowerCase()) &&
      matchesStatus(c.status, unifiedStatusFilter)
    );
  }, [unifiedCampaigns, unifiedSearch, unifiedStatusFilter]);

  const handleToggleUnified = async (campaign, e) => {
    e.stopPropagation();
    if (togglingUnifiedId) return;
    const p = campaign.platform;
    const curr = isActive(campaign.status);
    setTogglingUnifiedId(campaign.id);
    try {
      let newStatus;
      if (p === "tiktok") {
        newStatus = curr ? "DISABLE" : "ENABLE";
        await postRequest(`/api/ads/tiktok/campaign/${campaign.platform_campaign_id}/status`, { status: newStatus });
      } else if (p === "meta") {
        newStatus = curr ? "PAUSED" : "ACTIVE";
        await postRequest(`/api/ads/meta/campaign/${campaign.platform_campaign_id}/status`, { status: newStatus });
      } else if (p === "snapchat") {
        newStatus = curr ? "PAUSED" : "ACTIVE";
        await postRequest(`/api/ads/snapchat/campaign/${campaign.platform_campaign_id}/status`, { status: newStatus });
      } else if (p === "google") {
        newStatus = curr ? "PAUSED" : "ACTIVE";
        await postRequest(`/api/ads/google/campaign/${campaign.platform_campaign_id}/status`, { status: curr ? "PAUSED" : "ENABLED" });
      } else if (p === "taboola") {
        newStatus = curr ? "PAUSED" : "ACTIVE";
        await postRequest(`/api/ads/taboola/campaign/${campaign.platform_campaign_id}/status`, { is_active: !curr });
      } else {
        toast.info("Toggle not supported for this platform.");
        return;
      }
      setUnifiedCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, status: newStatus } : c));
      toast.success(`Campaign ${curr ? "paused" : "resumed"}.`);
    } catch (err) {
      toast.error(err.message || "Failed to update status.");
    } finally {
      setTogglingUnifiedId(null);
    }
  };

  const handleExportUnifiedCSV = () => {
    const headers = ["Platform", "Name", "Status", "Spend", "Impressions", "Clicks", "CTR", "Revenue", "ROAS", "Profit", "Orders", "Budget"];
    const rows = filteredUnified.map(c => {
      const statusLabel = c.status === "ENABLE" ? "ACTIVE" : c.status === "DISABLE" ? "PAUSED" : (c.status || "");
      const budget = c.daily_budget ? `$${c.daily_budget.toFixed(0)}/day` : (c.lifetime_budget ? `$${c.lifetime_budget.toFixed(0)} life.` : "");
      return [
        c.platform,
        `"${(c.name || "").replace(/"/g, '""')}"`,
        statusLabel,
        c.spend ?? "",
        c.impressions ?? "",
        c.clicks ?? "",
        c.ctr != null ? `${c.ctr}%` : "",
        c.revenue ?? "",
        c.roas != null ? c.roas : "",
        c.profit ?? "",
        c.orders ?? "",
        budget,
      ].join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `all_campaigns_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    if (platform === "all") {
      loadUnifiedCampaigns(preset);
    } else if (platform && campaignsRef.current.length > 0) {
      loadInsights(platform, campaignsRef.current, preset);
    }
  };

  // ── Load Campaigns ────────────────────────────────────────────────

  const loadCampaigns = useCallback(async (p, liAccId, preset) => {
    setCampaignsLoading(true);
    setCampaigns([]); setSelectedCampaign(null); setAdGroups([]);
    setSelectedAdGroup(null); setAds([]);
    setAdGroupSearch(""); setAdSearch(""); setCampaignStatusFilter("ALL");
    setMetrics([]); setSelectedIds([]);

    try {
      let result, items;
      if (p === "tiktok") {
        result = await getRequest("/api/ads/tiktok/campaigns");
        items = (result?.data || []).map(c => ({
          id: String(c.campaign_id), name: c.campaign_name || "—",
          status: (c.operation_status || c.status || "UNKNOWN").toUpperCase(), objective: c.objective_type || "",
          budget: c.budget || 0, budgetMode: c.budget_mode || "", budgetValue: c.budget || 0,
        }));
      } else if (p === "meta") {
        result = await getRequest("/api/ads/meta/campaigns");
        items = (result?.data || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: (c.status || "UNKNOWN").toUpperCase(), objective: c.objective || "",
          dailyBudget: c.daily_budget ? parseInt(c.daily_budget) : 0,
          lifetimeBudget: c.lifetime_budget ? parseInt(c.lifetime_budget) : 0,
          budgetValue: c.daily_budget ? parseInt(c.daily_budget)/100 : (c.lifetime_budget ? parseInt(c.lifetime_budget)/100 : 0),
        }));
      } else if (p === "linkedin") {
        result = await getRequest(`/api/ads/linkedin/campaign-groups?adAccountId=${liAccId}`);
        items = (result?.data?.items || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: (c.status || "UNKNOWN").toUpperCase(), budgetValue: 0,
        }));
      } else if (p === "snapchat") {
        result = await getRequest("/api/ads/snapchat/campaigns");
        items = (result?.data || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: (c.status || "UNKNOWN").toUpperCase(),
          dailyBudgetMicro: c.daily_budget_micro || 0,
          lifetimeMicro: c.lifetime_spend_cap_micro || 0,
          budgetValue: c.daily_budget_micro ? c.daily_budget_micro/1e6 : (c.lifetime_spend_cap_micro ? c.lifetime_spend_cap_micro/1e6 : 0),
        }));
      } else if (p === "google") {
        result = await getRequest("/api/ads/google/campaigns");
        items = (result?.data || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: c.status === "ENABLED" ? "ACTIVE" : (c.status || "UNKNOWN").toUpperCase(),
          objective: c.objective || "",
          budgetValue: c.budgetValue || 0,
        }));
      } else if (p === "shopify-oauth") {
        result = await getRequest("/api/ads/shopify-oauth/fetch-shopify-oauth-ads");
        const raw = Array.isArray(result?.data?.ads) ? result.data.ads : [];
        items = raw.map(c => ({
          id: String(c.id),
          name: c.description || "—",
          status: c.id === result?.data?.selected_ad_id ? "ACTIVE" : "PAUSED",
          budgetValue: 0,
        }));
      } else if (p === "taboola") {
        result = await getRequest("/api/ads/taboola/campaigns");
        items = (result?.campaigns || []).map(c => ({
          id: String(c.id), name: c.name || "—",
          status: c.is_active ? "ACTIVE" : "PAUSED",
          budgetValue: c.daily_cap || 0,
        }));
      }
      setCampaigns(items || []);
      if (items && items.length > 0) await loadInsights(p, items, preset);
    } catch (err) {
      toast.error(err.message || "Failed to load campaigns.");
    } finally {
      setCampaignsLoading(false);
    }
  }, [loadInsights]);

  const handleSelectPlatform = async (p) => {
    if (platformLoading) return;
    setPlatform(p);
    // Fetch last sync status so we can warn if the most recent sync failed
    getRequest("/api/sync/status")
      .then(res => {
        const errs = {};
        for (const [plat, info] of Object.entries(res?.data || {})) {
          if (info.lastSyncError) errs[plat] = info.lastSyncError;
        }
        setSyncErrors(errs);
      })
      .catch(() => {});
    setCampaignSearch(""); setAdGroupSearch(""); setAdSearch("");
    setCampaignStatusFilter("ALL"); setAdGroupStatusFilter("ALL"); setAdStatusFilter("ALL");
    setCampaignSort({ key: null, dir: "asc" }); setAdGroupSort({ key: null, dir: "asc" });
    setSelectedCampaign(null); setAdGroups([]); setSelectedAdGroup(null); setAds([]);
    setSelectedIds([]);
    if (p === "all") { setUnifiedSearch(""); setUnifiedStatusFilter("ALL"); await loadUnifiedCampaigns(datePreset); return; }
    if (p === "google") { await loadCampaigns(p, null, datePreset); return; }
    if (p === "linkedin") {
      setPlatformLoading(true);
      try {
        const r = await getRequest("/api/ads/linkedin/ad-accounts");
        const accId = r?.data?.selectedLinkedInAdvertiserId;
        if (!accId) throw new Error("No LinkedIn Ad Account selected. Go to Connected Platforms to configure it.");
        setLinkedInAdAccountId(accId);
        await loadCampaigns(p, accId, datePreset);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setPlatformLoading(false);
      }
    } else {
      await loadCampaigns(p, null, datePreset);
    }
  };

  // ── Campaign → Ad Groups ──────────────────────────────────────────

  const handleSelectCampaign = async (campaign) => {
    if (selectedCampaign?.id === campaign.id) {
      setSelectedCampaign(null); setAdGroups([]); setSelectedAdGroup(null); setAds([]);
      return;
    }
    setSelectedCampaign(campaign); setAdGroupsLoading(true);
    setAdGroups([]); setSelectedAdGroup(null); setAds([]);
    setAdGroupSearch(""); setAdGroupStatusFilter("ALL");

    try {
      let result, items;
      if (platform === "tiktok") {
        result = await getRequest(`/api/ads/tiktok/adgroups?campaign_id=${campaign.id}`);
        items = (result?.data || []).map(g => ({ id: String(g.adgroup_id), name: g.adgroup_name || "—", status: (g.operation_status || g.status || "UNKNOWN").toUpperCase() }));
      } else if (platform === "meta") {
        result = await getRequest(`/api/ads/meta/adsets?campaign_id=${campaign.id}`);
        items = (result?.data || []).map(g => ({ id: String(g.id), name: g.name || "—", status: (g.status || "UNKNOWN").toUpperCase() }));
      } else if (platform === "linkedin") {
        result = await getRequest(`/api/ads/linkedin/campaigns?adAccountId=${linkedInAdAccountId}`);
        items = (result?.data?.items || []).map(g => ({ id: String(g.id), name: g.name || "—", status: (g.status || "UNKNOWN").toUpperCase() }));
      } else if (platform === "snapchat") {
        result = await getRequest(`/api/ads/snapchat/adsquads?campaign_id=${campaign.id}`);
        items = (result?.data || []).map(g => ({ id: String(g.id), name: g.name || "—", status: (g.status || "UNKNOWN").toUpperCase() }));
      } else if (platform === "taboola") {
        result = await getRequest(`/api/ads/taboola/campaign/${campaign.id}/items`);
        items = (result?.items || []).map(i => ({
          id: String(i.id),
          name: i.title || i.url || "—",
          status: i.status || (i.is_active ? "RUNNING" : "PAUSED"),
          url: i.url,
          thumbnail_url: i.thumbnail_url,
        }));
      }
      setAdGroups(items || []);
    } catch (err) {
      toast.error(err.message || `Failed to load ${adGroupLabel.toLowerCase()}.`);
    } finally {
      setAdGroupsLoading(false);
    }
  };

  // ── Ad Group → Ads ────────────────────────────────────────────────

  const handleSelectAdGroup = async (adGroup) => {
    if (platform === "taboola") {
      if (selectedAdGroup?.id === adGroup.id) { setSelectedAdGroup(null); return; }
      setSelectedAdGroup(adGroup);
      return;
    }
    if (selectedAdGroup?.id === adGroup.id) { setSelectedAdGroup(null); setAds([]); return; }
    setSelectedAdGroup(adGroup); setAdsLoading(true);
    setAds([]); setAdSearch(""); setAdStatusFilter("ALL");

    try {
      let result;
      if (platform === "tiktok")        result = await getRequest(`/api/ads/tiktok/ads?adgroup_id=${adGroup.id}`);
      else if (platform === "meta")     result = await getRequest(`/api/ads/meta/ads?adset_id=${adGroup.id}`);
      else if (platform === "linkedin") result = await getRequest(`/api/ads/linkedin/ads?adAccountId=${linkedInAdAccountId}`);
      else if (platform === "snapchat") result = await getRequest(`/api/ads/snapchat/ads?adsquad_id=${adGroup.id}`);
      setAds(result?.data || []);
    } catch (err) {
      toast.error(err.message || `Failed to load ${adLabel.toLowerCase()}.`);
    } finally {
      setAdsLoading(false);
    }
  };

  // ── Taboola: Create Item ──────────────────────────────────────────

  const openCreateItemModal = () => {
    setCreateItemForm({ title: '', url: '', thumbnail_url: '', description: '', cta: '' });
    setCreateItemFile(null);
    setCreateItemPreview('');
    setCreateItemImageMode('url');
    setShowCreateItemModal(true);
  };

  const handleCreateItemFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCreateItemFile(file);
    setCreateItemPreview(URL.createObjectURL(file));
  };

  const handleCreateTaboolaItem = async () => {
    if (!createItemForm.url) { toast.error('Destination URL is required.'); return; }
    if (!createItemForm.title) { toast.error('Title is required.'); return; }
    setCreateItemLoading(true);
    try {
      let thumbnailCdn = createItemForm.thumbnail_url;

      // Upload image first if needed
      if (createItemImageMode === 'file' && createItemFile) {
        const fd = new FormData();
        fd.append('image', createItemFile);
        const upData = await postRequest('/api/ads/taboola/upload-image', fd);
        thumbnailCdn = upData.image_url;
      } else if (createItemImageMode === 'url' && createItemForm.thumbnail_url) {
        const upData = await postRequest('/api/ads/taboola/upload-image-url', { image_url: createItemForm.thumbnail_url });
        thumbnailCdn = upData.image_url;
      }

      const payload = {
        url: createItemForm.url,
        title: createItemForm.title,
        ...(thumbnailCdn && { thumbnail_url: thumbnailCdn }),
        ...(createItemForm.description && { description: createItemForm.description }),
        ...(createItemForm.cta && { cta: createItemForm.cta }),
      };

      await postRequest(`/api/ads/taboola/campaign/${selectedCampaign.id}/items`, payload);
      toast.success('Item created. Status: CRAWLING — Taboola will review it shortly.');
      setShowCreateItemModal(false);

      // Refresh items list
      setAdGroupsLoading(true);
      const result = await getRequest(`/api/ads/taboola/campaign/${selectedCampaign.id}/items`);
      const items = (result?.items || []).map(i => ({
        id: String(i.id),
        name: i.title || i.url || '—',
        status: i.status || (i.is_active ? 'RUNNING' : 'PAUSED'),
        url: i.url,
        thumbnail_url: i.thumbnail_url,
      }));
      setAdGroups(items);
      setAdGroupsLoading(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create item.');
    } finally {
      setCreateItemLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <FontAwesomeIcon icon={faBullhorn} className="text-[#221C51]" />
          Campaign Manager
        </h2>
        <p className="text-sm text-gray-500 mt-1">Browse and manage campaigns across all your connected platforms</p>
        <hr className="border-t border-gray-200 mt-3" />
      </div>

      {/* Platform tabs + Date picker */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {PLATFORMS.map(p => {
            const isAct = platform === p.key;
            const isConnected = p.key === "all" || connectedSet.has(p.key);
            return (
              <button key={p.key} onClick={() => handleSelectPlatform(p.key)} disabled={platformLoading}
                title={!isConnected ? `${p.label} is not connected. Go to Platform Settings to connect it.` : p.label}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                  isAct
                    ? "bg-[#221C51] text-white shadow-md"
                    : isConnected
                      ? "bg-white border border-gray-200 text-gray-600 hover:border-[#221C51]/40 hover:shadow-sm"
                      : "bg-white border border-gray-200 text-gray-400 hover:border-gray-300"
                }`}>
                {p.icon
                  ? <FontAwesomeIcon icon={p.icon} className={isAct ? "text-white" : isConnected ? p.iconColor : "text-gray-300"} />
                  : <span className={`text-base leading-none ${!isConnected && !isAct ? "opacity-40" : ""}`}>{p.emoji}</span>
                }
                {p.label}
                {!isConnected && (
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className={`text-xs ${isAct ? "text-white/70" : "text-amber-400"}`}
                  />
                )}
                {isAct && (campaignsLoading || platformLoading) && <FontAwesomeIcon icon={faSpinner} spin className="text-xs ml-1 opacity-70" />}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl p-1">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs ml-1.5" />
          {DATE_PRESETS.map(dp => (
            <button key={dp.key} onClick={() => handleDatePresetChange(dp.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                datePreset === dp.key ? "bg-[#221C51] text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}>
              {dp.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Unified "All Platforms" Panel ── */}
      {platform === "all" && (
        <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Panel Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-[#221C51]">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-sm text-white uppercase tracking-wide">All Campaigns</span>
              {!unifiedLoading && (
                <span className="bg-white/20 text-white text-xs px-2.5 py-0.5 rounded-full font-medium">
                  {filteredUnified.length} / {unifiedCampaigns.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!unifiedLoading && unifiedCampaigns.length > 0 && (
                <button onClick={handleExportUnifiedCSV}
                  title="Export as CSV"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white text-xs rounded-lg hover:bg-white/20 border border-white/20 transition-colors">
                  <FontAwesomeIcon icon={faFileArrowDown} className="text-xs" />
                  CSV
                </button>
              )}
              <div className="flex gap-1">
                {STATUS_FILTERS.map(s => (
                  <button key={s} onClick={() => setUnifiedStatusFilter(s)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      unifiedStatusFilter === s ? "bg-white text-[#221C51]" : "bg-white/10 text-white hover:bg-white/20"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
              <SearchInput value={unifiedSearch} onChange={setUnifiedSearch} placeholder="Search campaigns..." />
              {(unifiedSearch || unifiedStatusFilter !== "ALL") && (
                <ResetFiltersButton onClick={() => { setUnifiedSearch(""); setUnifiedStatusFilter("ALL"); }} />
              )}
            </div>
          </div>

          {unifiedLoading ? (
            <div className="flex items-center justify-center py-16 gap-3 bg-white text-gray-400">
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-[#221C51]" />
              <span className="text-sm">Loading all campaigns...</span>
            </div>
          ) : filteredUnified.length === 0 ? (
            <div className="text-center py-14 bg-white text-gray-400 text-sm">
              {unifiedCampaigns.length === 0
                ? "No synced campaigns found. Make sure your platforms are connected and sync has run."
                : "No results match your filter"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="text-xs text-white/70 uppercase tracking-wider border-b border-[#221C51]/20 bg-[#221C51]/90">
                    <th className="px-4 py-3 text-left font-medium">Platform</th>
                    <th className="px-4 py-3 text-left font-medium">Campaign</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-right font-medium">Spend</th>
                    <th className="px-4 py-3 text-right font-medium">Impr.</th>
                    <th className="px-4 py-3 text-right font-medium">Clicks</th>
                    <th className="px-4 py-3 text-right font-medium">CTR</th>
                    <th className="px-4 py-3 text-right font-medium">Revenue</th>
                    <th className="px-4 py-3 text-right font-medium">ROAS</th>
                    <th className="px-4 py-3 text-right font-medium">Profit</th>
                    <th className="px-4 py-3 text-left font-medium">Budget</th>
                    <th className="px-4 py-3 text-center font-medium">Toggle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredUnified.map(c => {
                    const pDef = PLATFORMS.find(p => p.key === c.platform);
                    const isToggling = togglingUnifiedId === c.id;
                    const roasColor = breakevenRoas && c.roas != null
                      ? (c.roas >= breakevenRoas ? "border-l-green-400" : "border-l-red-400")
                      : "border-l-transparent";
                    const budget = c.daily_budget
                      ? `$${c.daily_budget.toFixed(0)}/day`
                      : c.lifetime_budget
                        ? `$${c.lifetime_budget.toFixed(0)} life.`
                        : "—";
                    return (
                      <tr key={c.id} className={`border-l-4 ${roasColor} hover:bg-gray-50/80 transition-colors`}>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                            {pDef?.icon
                              ? <FontAwesomeIcon icon={pDef.icon} className={`text-sm ${pDef.iconColor}`} />
                              : <span>{pDef?.emoji || "📡"}</span>
                            }
                            {c.platform}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800 max-w-[200px]">
                          <span className="block truncate" title={c.name}>{c.name}</span>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600">{c.spend > 0 ? fmtSpend(c.spend) : "—"}</td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600">{fmtNum(c.impressions)}</td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600">{fmtNum(c.clicks)}</td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600">{c.ctr != null ? fmtCTR(c.ctr) : "—"}</td>
                        <td className="px-4 py-3 text-right text-xs text-gray-700">{c.revenue > 0 ? fmtSpend(c.revenue) : "—"}</td>
                        <td className={`px-4 py-3 text-right text-xs font-semibold ${
                          c.roas == null ? "text-gray-300"
                            : breakevenRoas && c.roas >= breakevenRoas ? "text-green-600"
                            : breakevenRoas && c.roas >= breakevenRoas * 0.8 ? "text-amber-500"
                            : "text-red-500"
                        }`}>{c.roas != null ? `${c.roas.toFixed(2)}×` : "—"}</td>
                        <td className={`px-4 py-3 text-right text-xs font-medium ${
                          c.profit == null ? "text-gray-300" : c.profit >= 0 ? "text-green-600" : "text-red-500"
                        }`}>{c.profit != null ? fmtSpend(c.profit) : "—"}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{budget}</td>
                        <td className="px-4 py-3 text-center">
                          {isToggleable(c.status) ? (
                            <button onClick={e => handleToggleUnified(c, e)} disabled={!!togglingUnifiedId}
                              title={isActive(c.status) ? "Pause" : "Resume"}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all mx-auto disabled:opacity-40 ${
                                isActive(c.status)
                                  ? "bg-amber-50 text-amber-500 hover:bg-amber-100 border border-amber-200"
                                  : "bg-green-50 text-green-600 hover:bg-green-100 border border-green-200"
                              }`}>
                              <FontAwesomeIcon icon={isToggling ? faSpinner : (isActive(c.status) ? faPause : faPlay)} spin={isToggling} className="text-xs" />
                            </button>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* No Platform */}
      {!platform && (
        <div className="flex flex-col items-center justify-center py-24 text-gray-300">
          <FontAwesomeIcon icon={faLayerGroup} className="text-6xl mb-5" />
          <p className="text-base font-medium text-gray-400">Select a platform above to browse campaigns</p>
        </div>
      )}

      {/* ── Sync Error Banner ── */}
      {platform && platform !== "all" && syncErrors[platform] && (
        <div className="flex items-start gap-3 px-5 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 flex-shrink-0 text-amber-500" />
          <div>
            <span className="font-semibold">Last sync failed for {platform}: </span>
            {syncErrors[platform]}
            {syncErrors[platform].toLowerCase().includes('expired') && (
              <span className="ml-2 text-amber-700">— Go to <strong>Platform Settings</strong> to reconnect.</span>
            )}
          </div>
        </div>
      )}

      {/* ── Campaigns Panel ── */}
      {platform && platform !== "all" && (
        <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm">

          {/* Panel Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-[#221C51]">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-sm text-white uppercase tracking-wide">{campaignLabel}</span>
              {!campaignsLoading && (
                <span className="bg-white/20 text-white text-xs px-2.5 py-0.5 rounded-full font-medium">
                  {filteredCampaigns.length} / {campaigns.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Export CSV */}
              {!campaignsLoading && campaigns.length > 0 && (
                <button onClick={handleExportCSV}
                  title="Export current view as CSV"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white text-xs rounded-lg hover:bg-white/20 border border-white/20 transition-colors">
                  <FontAwesomeIcon icon={faFileArrowDown} className="text-xs" />
                  CSV
                </button>
              )}
              <div className="flex gap-1">
                {STATUS_FILTERS.map(s => (
                  <button key={s} onClick={() => { setCampaignStatusFilter(s); setSelectedIds([]); }}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      campaignStatusFilter === s ? "bg-white text-[#221C51]" : "bg-white/10 text-white hover:bg-white/20"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
              <SearchInput value={campaignSearch} onChange={v => { setCampaignSearch(v); setSelectedIds([]); }} placeholder={`Search ${campaignLabel.toLowerCase()}...`} />
              {(campaignSearch || campaignStatusFilter !== "ALL") && (
                <ResetFiltersButton onClick={() => { setCampaignSearch(""); setCampaignStatusFilter("ALL"); setSelectedIds([]); }} />
              )}
            </div>
          </div>

          {/* Summary Stats Bar */}
          {summaryStats && !campaignsLoading && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3 bg-gray-50 border-b border-gray-100">
              <SummaryStat label="Active"  value={summaryStats.active}  color="text-green-600" />
              <SummaryStat label="Paused"  value={summaryStats.paused}  color="text-amber-600" />
              {summaryStats.totalBudget > 0 && (
                <SummaryStat label="Total Budget" value={`$${summaryStats.totalBudget.toFixed(0)}`} />
              )}
              {summaryStats.hasMetrics && <>
                <div className="w-px h-6 bg-gray-200 mx-1" />
                <SummaryStat label="Spend"       value={fmtSpend(summaryStats.totalSpend)}     loading={metricsLoading} />
                <SummaryStat label="Impressions" value={fmtNum(summaryStats.totalImpr)}        loading={metricsLoading} />
                <SummaryStat label="Clicks"      value={fmtNum(summaryStats.totalClicks)}      loading={metricsLoading} />
                <SummaryStat label="Avg CTR"     value={fmtCTR(summaryStats.avgCTR)}           loading={metricsLoading} />
              </>}
            </div>
          )}

          {/* Bulk Action Bar */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 px-6 py-2.5 bg-[#221C51]/[0.05] border-b border-[#221C51]/10">
              <FontAwesomeIcon icon={faCircleCheck} className="text-[#221C51] text-sm" />
              <span className="text-xs font-semibold text-[#221C51]">{selectedIds.length} selected</span>
              <div className="flex gap-2 ml-auto">
                <button onClick={() => handleBulkAction("pause")} disabled={bulkLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors">
                  <FontAwesomeIcon icon={bulkLoading ? faSpinner : faPause} spin={bulkLoading} className="text-xs" />
                  Pause Selected
                </button>
                <button onClick={() => handleBulkAction("resume")} disabled={bulkLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
                  <FontAwesomeIcon icon={bulkLoading ? faSpinner : faPlay} spin={bulkLoading} className="text-xs" />
                  Resume Selected
                </button>
                <button onClick={() => setSelectedIds([])} disabled={bulkLoading}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                  <FontAwesomeIcon icon={faXmark} className="text-xs" />
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          {campaignsLoading ? (
            <div className="flex items-center justify-center py-16 gap-3 bg-white text-gray-400">
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-[#221C51]" />
              <span className="text-sm">Loading {campaignLabel.toLowerCase()}...</span>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="text-center py-14 bg-white text-gray-400 text-sm">
              {campaigns.length === 0 ? `No ${campaignLabel.toLowerCase()} found` : "No results match your filter"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="text-xs text-white/70 uppercase tracking-wider border-b border-[#221C51]/20 bg-[#221C51]/90">
                    {/* Select-all checkbox */}
                    <th className="px-4 py-3 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={handleSelectAll}
                        onClick={e => e.stopPropagation()}
                        className="w-3.5 h-3.5 rounded accent-white cursor-pointer"
                        title="Select all"
                      />
                    </th>
                    <th className="px-4 py-3 w-8" />
                    <SortableHeader label="Name"   sortKey="name"   sortConfig={campaignSort} onSort={handleCampaignSort} className="text-left" />
                    <SortableHeader label="Status" sortKey="status" sortConfig={campaignSort} onSort={handleCampaignSort} className="text-left" />
                    <SortableHeader label="Budget" sortKey="budget" sortConfig={campaignSort} onSort={handleCampaignSort} className="text-left" />
                    {hasInsights && <>
                      <SortableHeader label="Spend"  sortKey="spend"       sortConfig={campaignSort} onSort={handleCampaignSort} className="text-right" />
                      <SortableHeader label="Impr."  sortKey="impressions" sortConfig={campaignSort} onSort={handleCampaignSort} className="text-right" />
                      <SortableHeader label="Clicks" sortKey="clicks"      sortConfig={campaignSort} onSort={handleCampaignSort} className="text-right" />
                      <SortableHeader label="CTR"    sortKey="ctr"         sortConfig={campaignSort} onSort={handleCampaignSort} className="text-right" />
                    </>}
                    {(platform === "tiktok" || platform === "meta") && (
                      <th className="px-4 py-3 text-left font-medium text-xs text-white/70 uppercase tracking-wider">Objective</th>
                    )}
                    <th className="px-4 py-3 text-left font-medium text-xs text-white/70 uppercase tracking-wider">{adGroupLabel}</th>
                    <th className="px-4 py-3 text-center font-medium text-xs text-white/70 uppercase tracking-wider">Toggle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredCampaigns.map(c => {
                    const isSelected   = selectedCampaign?.id === c.id;
                    const isChecked    = selectedIds.includes(c.id);
                    const m            = metrics[c.id];
                    const isToggling   = togglingId === c.id;
                    return (
                      <tr key={c.id} onClick={() => handleSelectCampaign(c)}
                        className={`cursor-pointer transition-all ${
                          isChecked ? "bg-[#221C51]/[0.04]" : ""
                        } ${
                          isSelected ? "border-l-4 border-l-[#221C51]" : "border-l-4 border-l-transparent hover:bg-gray-50/80"
                        }`}>
                        {/* Row checkbox */}
                        <td className="px-4 py-4 text-center w-10" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={e => handleSelectOne(c.id, e)}
                            className="w-3.5 h-3.5 rounded accent-[#221C51] cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-4 text-gray-400 w-8">
                          <FontAwesomeIcon icon={isSelected ? faChevronDown : faChevronRight} className="text-xs" />
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-800 max-w-[180px]">
                          <span className="block truncate" title={c.name}>{c.name}</span>
                        </td>
                        <td className="px-4 py-4"><StatusBadge status={c.status} /></td>
                        <td className="px-4 py-4 text-xs text-gray-500 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                          {editingBudgetId === c.id ? (
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400">$</span>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={budgetInput}
                                onChange={e => setBudgetInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveBudget(c, e); if (e.key === 'Escape') cancelEditBudget(e); }}
                                autoFocus
                                className="w-20 px-1.5 py-1 text-xs border border-[#221C51]/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221C51]/20"
                              />
                              <button onClick={e => saveBudget(c, e)} disabled={!!savingBudgetId} title="Save"
                                className="w-6 h-6 flex items-center justify-center rounded-md bg-green-100 text-green-600 hover:bg-green-200 disabled:opacity-40">
                                <FontAwesomeIcon icon={savingBudgetId === c.id ? faSpinner : faCheck} spin={savingBudgetId === c.id} className="text-xs" />
                              </button>
                              <button onClick={cancelEditBudget} title="Cancel"
                                className="w-6 h-6 flex items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200">
                                <FontAwesomeIcon icon={faXmark} className="text-xs" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 group">
                              <span>{fmtBudget(c, platform)}</span>
                              {platform !== 'linkedin' && platform !== 'google' && (
                                <button onClick={e => startEditBudget(c, e)} title="Edit budget"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-[#221C51] hover:bg-gray-100">
                                  <FontAwesomeIcon icon={faPencil} className="text-[10px]" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        {hasInsights && <>
                          <td className="px-4 py-4 text-right"><MetricCell value={fmtSpend(m?.spend)}     loading={metricsLoading} /></td>
                          <td className="px-4 py-4 text-right"><MetricCell value={fmtNum(m?.impressions)} loading={metricsLoading} /></td>
                          <td className="px-4 py-4 text-right"><MetricCell value={fmtNum(m?.clicks)}      loading={metricsLoading} /></td>
                          <td className="px-4 py-4 text-right"><MetricCell value={fmtCTR(m?.ctr)}         loading={metricsLoading} /></td>
                        </>}
                        {(platform === "tiktok" || platform === "meta") && (
                          <td className="px-4 py-4 text-xs text-gray-400">{c.objective || "—"}</td>
                        )}
                        <td className="px-4 py-4">
                          <span className={`text-xs font-semibold ${isSelected ? "text-[#221C51]" : "text-gray-400"}`}>
                            {isSelected ? "Showing ▾" : "View →"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center" onClick={e => e.stopPropagation()}>
                          {isToggleable(c.status) ? (
                            <button onClick={e => handleToggleCampaign(c, e)} disabled={!!togglingId}
                              title={isActive(c.status) ? "Pause campaign" : "Resume campaign"}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all mx-auto disabled:opacity-40 ${
                                isActive(c.status)
                                  ? "bg-amber-50 text-amber-500 hover:bg-amber-100 border border-amber-200"
                                  : "bg-green-50 text-green-600 hover:bg-green-100 border border-green-200"
                              }`}>
                              <FontAwesomeIcon icon={isToggling ? faSpinner : (isActive(c.status) ? faPause : faPlay)} spin={isToggling} className="text-xs" />
                            </button>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Ad Groups Panel ── */}
      {(selectedCampaign !== null || adGroupsLoading) && (
        <div className="rounded-2xl border border-[#221C51]/20 overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 bg-[#221C51]/[0.06]">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-sm text-[#221C51] uppercase tracking-wide">{adGroupLabel}</span>
              {selectedCampaign && (
                <span className="text-xs text-gray-500">— <span className="font-medium text-gray-700">{selectedCampaign.name}</span></span>
              )}
              {!adGroupsLoading && (
                <span className="bg-[#221C51] text-white text-xs px-2.5 py-0.5 rounded-full font-medium">{filteredAdGroups.length}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {platform === "taboola" && selectedCampaign && (
                <button onClick={openCreateItemModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#221C51] text-white rounded-lg hover:bg-[#2d2570] transition-colors">
                  <FontAwesomeIcon icon={faImage} className="text-xs" />
                  Add Item
                </button>
              )}
              <div className="flex gap-1">
                {STATUS_FILTERS.map(s => (
                  <button key={s} onClick={() => setAdGroupStatusFilter(s)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      adGroupStatusFilter === s ? "bg-[#221C51] text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
              <SearchInput value={adGroupSearch} onChange={setAdGroupSearch} placeholder={`Search ${adGroupLabel.toLowerCase()}...`} light />
              {(adGroupSearch || adGroupStatusFilter !== "ALL") && (
                <ResetFiltersButton light onClick={() => { setAdGroupSearch(""); setAdGroupStatusFilter("ALL"); }} />
              )}
            </div>
          </div>
          {adGroupsLoading ? (
            <div className="flex items-center justify-center py-12 gap-3 bg-white text-gray-400">
              <FontAwesomeIcon icon={faSpinner} spin className="text-xl text-[#221C51]" />
              <span className="text-sm">Loading {adGroupLabel.toLowerCase()}...</span>
            </div>
          ) : filteredAdGroups.length === 0 ? (
            <div className="text-center py-10 bg-white text-gray-400 text-sm">No {adGroupLabel.toLowerCase()} found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="text-xs text-gray-400 uppercase tracking-wider border-b border-gray-100 bg-gray-50/80">
                    <th className="px-4 py-3 w-8" />
                    {platform === "taboola" && <th className="px-4 py-3 w-16 text-left font-medium">Image</th>}
                    <SortableHeaderLight label="Name"   sortKey="name"   sortConfig={adGroupSort} onSort={handleAdGroupSort} className="text-left" />
                    <SortableHeaderLight label="Status" sortKey="status" sortConfig={adGroupSort} onSort={handleAdGroupSort} className="text-left" />
                    {platform === "taboola"
                      ? <th className="px-4 py-3 text-left font-medium">URL</th>
                      : <th className="px-4 py-3 text-left font-medium">ID</th>
                    }
                    {platform !== "taboola" && <th className="px-4 py-3 text-left font-medium">{adLabel}</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredAdGroups.map(g => {
                    const isAdGroupSelected = selectedAdGroup?.id === g.id;
                    return (
                      <tr key={g.id} onClick={() => handleSelectAdGroup(g)}
                        className={`cursor-pointer transition-all ${
                          isAdGroupSelected ? "bg-[#221C51]/5 border-l-4 border-l-[#221C51]" : "hover:bg-gray-50/80 border-l-4 border-l-transparent"
                        }`}>
                        <td className="px-4 py-4 text-gray-400 w-8">
                          <FontAwesomeIcon icon={isAdGroupSelected ? faChevronDown : faChevronRight} className="text-xs" />
                        </td>
                        {platform === "taboola" && (
                          <td className="px-4 py-4 w-16">
                            <AdThumbnail url={g.thumbnail_url} />
                          </td>
                        )}
                        <td className="px-4 py-4 text-sm font-medium text-gray-800 max-w-[260px]">
                          <span className="block truncate" title={g.name}>{g.name}</span>
                        </td>
                        <td className="px-4 py-4"><StatusBadge status={g.status} /></td>
                        {platform === "taboola" ? (
                          <td className="px-4 py-4 text-xs text-gray-500 max-w-[200px]" onClick={e => e.stopPropagation()}>
                            <a href={g.url} target="_blank" rel="noopener noreferrer"
                              className="truncate block hover:text-[#221C51] hover:underline" title={g.url}>
                              {g.url}
                            </a>
                          </td>
                        ) : (
                          <>
                            <td className="px-4 py-4 text-xs text-gray-400 font-mono">{g.id}</td>
                            <td className="px-4 py-4">
                              <span className={`text-xs font-semibold ${isAdGroupSelected ? "text-[#221C51]" : "text-gray-400"}`}>
                                {isAdGroupSelected ? "Showing ▾" : "View →"}
                              </span>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Ads Panel (3rd level) — not shown for Taboola, items are the terminal level ── */}
      {(selectedAdGroup !== null || adsLoading) && platform !== "taboola" && (
        <div className="rounded-2xl border border-gray-200/80 overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faRectangleAd} className="text-[#221C51]/60 text-sm" />
              <span className="font-semibold text-sm text-gray-700 uppercase tracking-wide">{adLabel}</span>
              {selectedAdGroup && (
                <span className="text-xs text-gray-500">— <span className="font-medium text-gray-700">{selectedAdGroup.name}</span></span>
              )}
              {!adsLoading && (
                <span className="bg-gray-200 text-gray-600 text-xs px-2.5 py-0.5 rounded-full font-medium">{filteredAds.length}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                {STATUS_FILTERS.map(s => (
                  <button key={s} onClick={() => setAdStatusFilter(s)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      adStatusFilter === s ? "bg-[#221C51] text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
              <SearchInput value={adSearch} onChange={setAdSearch} placeholder={`Search ${adLabel.toLowerCase()}...`} light />
              {(adSearch || adStatusFilter !== "ALL") && (
                <ResetFiltersButton light onClick={() => { setAdSearch(""); setAdStatusFilter("ALL"); }} />
              )}
            </div>
          </div>
          {adsLoading ? (
            <div className="flex items-center justify-center py-10 gap-3 bg-white text-gray-400">
              <FontAwesomeIcon icon={faSpinner} spin className="text-xl text-[#221C51]" />
              <span className="text-sm">Loading {adLabel.toLowerCase()}...</span>
            </div>
          ) : filteredAds.length === 0 ? (
            <div className="text-center py-9 bg-white text-gray-400 text-sm">No {adLabel.toLowerCase()} found</div>
          ) : (
            <table className="min-w-full bg-white">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wider border-b border-gray-100 bg-gray-50/60">
                  <th className="px-5 py-3 text-left font-medium w-14">Preview</th>
                  <th className="px-5 py-3 text-left font-medium">Name</th>
                  <th className="px-5 py-3 text-left font-medium">Status</th>
                  {(platform === "tiktok" || platform === "snapchat") && (
                    <th className="px-5 py-3 text-left font-medium">Format / Type</th>
                  )}
                  <th className="px-5 py-3 text-left font-medium">ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredAds.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3"><AdThumbnail url={a.thumbnailUrl} /></td>
                    <td className="px-5 py-4 text-sm font-medium text-gray-800 max-w-[280px]">
                      <span className="block truncate" title={a.name}>{a.name}</span>
                    </td>
                    <td className="px-5 py-4"><StatusBadge status={a.status} /></td>
                    {(platform === "tiktok" || platform === "snapchat") && (
                      <td className="px-5 py-4 text-xs text-gray-400">{a.format || a.type || "—"}</td>
                    )}
                    <td className="px-5 py-4 text-xs text-gray-400 font-mono">{a.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {/* ── Taboola Create Item Modal ── */}
      {showCreateItemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-[#221C51]">
              <span className="font-semibold text-white text-sm uppercase tracking-wide">Add Taboola Item</span>
              <button onClick={() => setShowCreateItemModal(false)} className="text-white/60 hover:text-white transition-colors">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Title <span className="text-red-500">*</span></label>
                <input type="text" placeholder="Ad headline" value={createItemForm.title}
                  onChange={e => setCreateItemForm(f => ({ ...f, title: e.target.value }))}
                  className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#221C51]/20" />
              </div>

              {/* Destination URL */}
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Destination URL <span className="text-red-500">*</span></label>
                <input type="url" placeholder="https://yoursite.com/page" value={createItemForm.url}
                  onChange={e => setCreateItemForm(f => ({ ...f, url: e.target.value }))}
                  className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#221C51]/20" />
              </div>

              {/* Thumbnail */}
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Thumbnail Image</label>
                <div className="flex gap-2 mb-2">
                  <button onClick={() => setCreateItemImageMode('url')}
                    className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${createItemImageMode === 'url' ? 'bg-[#221C51] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    Image URL
                  </button>
                  <button onClick={() => setCreateItemImageMode('file')}
                    className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${createItemImageMode === 'file' ? 'bg-[#221C51] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    Upload File
                  </button>
                </div>
                {createItemImageMode === 'url' ? (
                  <input type="url" placeholder="https://cdn.example.com/image.jpg" value={createItemForm.thumbnail_url}
                    onChange={e => setCreateItemForm(f => ({ ...f, thumbnail_url: e.target.value }))}
                    className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#221C51]/20" />
                ) : (
                  <input type="file" accept="image/*" onChange={handleCreateItemFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#221C51] file:text-white hover:file:bg-[#2d2570]" />
                )}
                {(createItemPreview || (createItemImageMode === 'url' && createItemForm.thumbnail_url)) && (
                  <img
                    src={createItemPreview || createItemForm.thumbnail_url}
                    alt="preview"
                    className="mt-2 h-20 w-auto rounded-lg border border-gray-200 object-cover"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>

              {/* Description (optional) */}
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
                <input type="text" placeholder="Short ad description" value={createItemForm.description}
                  onChange={e => setCreateItemForm(f => ({ ...f, description: e.target.value }))}
                  className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#221C51]/20" />
              </div>

              {/* CTA (optional) */}
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">CTA <span className="text-gray-400 font-normal">(optional)</span></label>
                <select value={createItemForm.cta} onChange={e => setCreateItemForm(f => ({ ...f, cta: e.target.value }))}
                  className="block w-full p-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#221C51]/20">
                  <option value="">None</option>
                  <option value="LEARN_MORE">Learn More</option>
                  <option value="SHOP_NOW">Shop Now</option>
                  <option value="SIGN_UP">Sign Up</option>
                  <option value="DOWNLOAD">Download</option>
                  <option value="GET_QUOTE">Get Quote</option>
                  <option value="CONTACT_US">Contact Us</option>
                  <option value="WATCH_MORE">Watch More</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button onClick={() => setShowCreateItemModal(false)} disabled={createItemLoading}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleCreateTaboolaItem} disabled={createItemLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-[#221C51] text-white rounded-lg hover:bg-[#2d2570] disabled:opacity-50 transition-colors">
                <FontAwesomeIcon icon={createItemLoading ? faSpinner : faImage} spin={createItemLoading} className="text-xs" />
                {createItemLoading ? 'Creating...' : 'Create Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManager;
